'use client';

import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Check if user dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) return;

    // Detect iOS
    const ua = window.navigator.userAgent;
    const iosDevice = /iphone|ipad|ipod/i.test(ua);
    const safariOnly = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);

    if (iosDevice && safariOnly) {
      setIsIOS(true);
      setShowBanner(true);
      return;
    }

    // Listen for Chrome/Android install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setIsInstalled(true);
    }
    setShowBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', '1');
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 lg:left-60 z-50 p-3 sm:p-4">
      <div className="max-w-md mx-auto bg-gray-900 border border-violet-700/60 rounded-2xl shadow-2xl shadow-violet-900/40 p-4 flex items-start gap-3">
        {/* App icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-violet-900/50 border border-violet-700/40 flex items-center justify-center">
          <img
            src="/assets/images/app_logo.png"
            alt="BizzBox app icon"
            className="w-10 h-10 object-contain"
          />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm leading-tight">Add BizzBox to Home Screen</p>
          {isIOS ? (
            <p className="text-gray-400 text-xs mt-1 leading-snug">
              Tap the <span className="text-violet-400 font-medium">Share</span> button then{' '}
              <span className="text-violet-400 font-medium">"Add to Home Screen"</span> to install.
            </p>
          ) : (
            <p className="text-gray-400 text-xs mt-1 leading-snug">
              Install the app for a faster, native-like experience.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              Install
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 text-xs rounded-lg transition-colors"
          >
            {isIOS ? 'Got it' : 'Not now'}
          </button>
        </div>
      </div>
    </div>
  );
}
